import * as Draft from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { action, computed, makeObservable, observable } from 'mobx';
import * as React from 'react';

import { FIELD_PLACEHOLDER_ENTITY, FieldPlaceholderDecorator } from './FieldPlaceholderDecorator';

/**
 * Информация о поле формы для вставки в редактор
 */
export type NotificationFieldInfo = {
  id: string;
  name: string;
};

/**
 * Декоратор для ссылок
 */
const findLinkEntities = (
  contentBlock: Draft.ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: Draft.ContentState
) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === 'LINK'
    );
  }, callback);
};

const LinkComponent: React.FC<{
  contentState: Draft.ContentState;
  entityKey: string;
  children: React.ReactNode;
}> = ({ contentState, entityKey, children }) => {
  const { url } = contentState.getEntity(entityKey).getData();
  return React.createElement(
    'a',
    { href: url, style: { color: '#1890ff', textDecoration: 'underline' } },
    children
  );
};

/**
 * Комбинированный декоратор для ссылок и плейсхолдеров полей
 */
const notificationDecorator = new Draft.CompositeDecorator([
  {
    strategy: findLinkEntities,
    component: LinkComponent,
  },
  FieldPlaceholderDecorator,
]);

type PrivateFields = '_editorState' | '_removeEntity' | '_setSelectionPosition';

/**
 * Модель для управления состоянием редактора уведомлений
 */
export class NotificationEditorModel {
  constructor() {
    this._editorState = Draft.EditorState.createEmpty(notificationDecorator);

    makeObservable<NotificationEditorModel, PrivateFields>(this, {
      _editorState: observable.ref,
      editorState: computed,
      setEditorState: action.bound,
      hasContent: computed,
      toServerFormat: computed,
      addFieldPlaceholder: action.bound,
      _removeEntity: action.bound,
      _setSelectionPosition: action,
      reset: action.bound,
    });
  }

  private _editorState: Draft.EditorState;

  /**
   * Состояние редактора Draft.js
   */
  get editorState(): Draft.EditorState {
    return this._editorState;
  }

  /**
   * Проверяет, есть ли контент в редакторе
   */
  get hasContent(): boolean {
    return this._editorState.getCurrentContent().hasText();
  }

  /**
   * Конвертирует содержимое редактора в HTML с плейсхолдерами {{field_id|field_name}}
   * Формат {{id|название}} позволяет:
   * - Бэкенду извлечь ID для подстановки значений
   * - Отображать название столбца в истории уведомлений
   */
  get toServerFormat(): string {
    const contentState = this._editorState.getCurrentContent();

    const options = {
      entityStyleFn: (entity: Draft.EntityInstance) => {
        const entityType = entity.getType();

        if (entityType === 'LINK') {
          const data = entity.getData();
          return {
            element: 'a',
            attributes: {
              href: data.url,
              target: '_blank',
              rel: 'noopener noreferrer',
            },
          };
        }

        if (entityType === FIELD_PLACEHOLDER_ENTITY) {
          const data = entity.getData();
          // Возвращаем плейсхолдер с ID для замены
          return {
            element: 'span',
            attributes: {
              'data-field-id': data.fieldId,
            },
            style: {},
          };
        }

        return undefined;
      },
    };

    let html = stateToHTML(contentState, options);

    // Заменяем span с data-field-id на плейсхолдеры {{id|название}}
    // stateToHTML создаёт <span data-field-id="123" ...>Название поля</span>
    // Нужно заменить на {{123|Название поля}}
    html = html.replace(
      /<span data-field-id="(\d+)"[^>]*>([^<]*)<\/span>/g,
      '{{$1|$2}}'
    );

    return html;
  }

  /**
   * Устанавливает состояние редактора
   */
  setEditorState(editorState: Draft.EditorState): void {
    this._editorState = editorState;
  }

  /**
   * Добавляет плейсхолдер поля в текущую позицию курсора
   */
  addFieldPlaceholder(field: NotificationFieldInfo): void {
    // Создаём Draft.js сущность с данными поля
    const contentStateWithEntity = this._editorState
      .getCurrentContent()
      .createEntity(FIELD_PLACEHOLDER_ENTITY, 'IMMUTABLE', {
        fieldId: field.id,
        onRemoveEntity: this._removeEntity,
      });

    // Получаем уникальный ключ Entity
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

    // Объединяем текущее состояние с новым
    const editorStateWithEntity = Draft.EditorState.set(this._editorState, {
      currentContent: contentStateWithEntity,
      decorator: notificationDecorator,
    });

    // Вставляем текст (название поля) с привязкой к сущности
    const newContentState = Draft.Modifier.replaceText(
      editorStateWithEntity.getCurrentContent(),
      editorStateWithEntity.getSelection(),
      field.name,
      undefined,
      entityKey
    );

    this._editorState = Draft.EditorState.set(editorStateWithEntity, {
      currentContent: newContentState,
      decorator: notificationDecorator,
    });

    // После вставки текста, устанавливаем курсор на позицию справа от текста
    this._setSelectionPosition({
      newOffset: this._editorState.getSelection().getAnchorOffset() + field.name.length,
    });
  }

  /**
   * Устанавливает позицию курсора
   */
  private _setSelectionPosition({
    newFocus,
    newOffset,
  }: {
    newOffset: number;
    newFocus?: number;
  }): void {
    const currentSelection = this._editorState.getSelection();

    this._editorState = Draft.EditorState.forceSelection(
      this._editorState,
      currentSelection.merge({
        anchorOffset: newOffset,
        focusOffset: newFocus ?? newOffset,
      })
    );
  }

  /**
   * Удаляет сущность по её ключу
   */
  private _removeEntity(draftEntityId: string): void {
    const contentState = this._editorState.getCurrentContent();
    const blockMap = contentState.getBlockMap();

    let foundEntity: { start: number; end: number; blockKey: string } | null = null;

    // Ищем сущность в блоках
    blockMap.forEach((block) => {
      if (!block || foundEntity) return;

      block.findEntityRanges(
        (character) => character.getEntity() === draftEntityId,
        (start, end) => {
          foundEntity = { start, end, blockKey: block.getKey() };
        }
      );
    });

    if (!foundEntity) {
      return;
    }

    const { start, end, blockKey } = foundEntity;

    const selection = Draft.SelectionState.createEmpty(blockKey).merge({
      anchorOffset: start,
      focusOffset: end,
    });

    const newContentState = Draft.Modifier.removeRange(
      contentState,
      selection,
      'forward'
    );

    this._editorState = Draft.EditorState.set(this._editorState, {
      currentContent: newContentState,
    });

    // После удаления, ставим курсор на позицию удалённого текста
    this._setSelectionPosition({
      newOffset: start,
    });
  }

  /**
   * Очищает редактор
   */
  reset(): void {
    this._editorState = Draft.EditorState.createEmpty(notificationDecorator);
  }
}
